import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, CheckCircle, Clock, AlertCircle, UserCheck, Upload, FileText, MessageSquare, Video, Eye, X, RefreshCw, Phone, User } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

const LecturerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [studentsForUnit, setStudentsForUnit] = useState([]);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedUnitForDecline, setSelectedUnitForDecline] = useState(null);

  useEffect(() => {
    fetchAssignedUnits();
  }, []);

  const fetchAssignedUnits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned units');
      }

      const data = await response.json();
      setAssignedUnits(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAssignment = async (unitId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${unitId}/accept-assignment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ campusId: user.campusId })
      });

      if (response.ok) {
        fetchAssignedUnits(); // Refresh data
        alert('Assignment accepted successfully!');
      } else {
        alert('Failed to accept assignment');
      }
    } catch (err) {
      alert('Error accepting assignment');
    }
  };

  const handleDeclineAssignment = (unit) => {
    setSelectedUnitForDecline(unit);
    setDeclineReason('');
    setShowDeclineModal(true);
  };

  const submitDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining the assignment.');
      return;
    }

    if (declineReason.trim().length < 10) {
      alert('Decline reason must be at least 10 characters long.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${selectedUnitForDecline.id}/decline-assignment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ declineReason: declineReason.trim() })
      });

      if (response.ok) {
        setShowDeclineModal(false);
        fetchAssignedUnits(); // Refresh data
        alert('Assignment declined successfully!');
      } else {
        alert('Failed to decline assignment');
      }
    } catch (err) {
      alert('Error declining assignment');
    }
  };

  const fetchStudentsForUnit = async (unitId, campusId) => {
    setFetchingStudents(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/student-units/byUnitAndCampus?unitId=${unitId}&campusId=${campusId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data = await response.json();
      setStudentsForUnit(data);
    } catch (err) {
      console.error('Error fetching students:', err);
      alert('Failed to load students');
    } finally {
      setFetchingStudents(false);
    }
  };

  const handleViewStudents = (unit) => {
    setSelectedUnit(unit);
    // Use the assignment-specific campusId, fallback to lecturer's campusId if not available
    fetchStudentsForUnit(unit.id, unit.campusId || unit.lecturer?.campusId);
    setShowStudentsModal(true);
  };

  const handleCompleteUnitForStudent = async (studentId, unitId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/student-units/complete-unit/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ grade: 'N/A' })
      });

      if (response.ok) {
        alert('Student marked as completed!');
        // Refresh students list using the assignment-specific campusId
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.campusId || selectedUnit.lecturer?.campusId);
      } else {
        alert('Failed to mark student as completed');
      }
    } catch (err) {
      console.error('Error completing unit:', err);
      alert('Error marking student as completed');
    }
  };

  const handleApproveCashPayment = async (studentId, unitId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/approve-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lecturerId: user.id,
          approvalNotes: 'Approved by lecturer via dashboard'
        })
      });

      if (response.ok) {
        alert('Cash payment approved successfully!');
        // Refresh students list
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.lecturer?.campusId);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to approve cash payment');
      }
    } catch (err) {
      console.error('Error approving cash payment:', err);
      alert('Error approving cash payment');
    }
  };

  const handleRejectCashPayment = async (studentId, unitId) => {
    const reason = prompt('Please provide a reason for rejecting this payment:');
    if (!reason || !reason.trim()) {
      alert('Rejection reason is required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/reject-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lecturerId: user.id,
          rejectionNotes: reason.trim()
        })
      });

      if (response.ok) {
        alert('Cash payment rejected');
        // Refresh students list
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.lecturer?.campusId);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to reject cash payment');
      }
    } catch (err) {
      console.error('Error rejecting cash payment:', err);
      alert('Error rejecting cash payment');
    }
  };

  const handleManageContent = (unit) => {
    // Navigate to materials page with unit context
    navigate(`/materials?unitId=${unit.id}`);
  };

  const handleUploadMaterials = () => {
    navigate('/materials');
  };

  const handleCreateAssignments = () => {
    navigate('/assignments');
  };

  const handleDiscussionForum = () => {
    alert('Discussion Forum feature coming soon!');
  };

  const handleVirtualClassroom = () => {
    navigate('/virtual-classroom');
  };


  const getStudentStatus = (student) => {
    if (student.paymentStatus === 'confirmed') {
      if (student.status === 'completed') {
        return { text: 'Completed', color: 'text-green-600 bg-green-100' };
      } else if (student.status === 'in-progress') {
        return { text: 'In Progress', color: 'text-blue-600 bg-blue-100' };
      } else {
        return { text: 'Paid - Not Started', color: 'text-yellow-600 bg-yellow-100' };
      }
    } else if (student.paymentStatus === 'pending') {
      return { text: 'Payment Pending', color: 'text-orange-600 bg-orange-100' };
    } else {
      return { text: 'Not Enrolled', color: 'text-gray-600 bg-gray-100' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepted':
        return 'Active';
      case 'pending':
        return 'Pending Acceptance';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  const getLecturerSummary = () => {
    const totalUnits = assignedUnits.length;
    const pendingUnits = assignedUnits.filter(unit => unit.assignmentStatus === 'pending').length;
    const activeUnits = assignedUnits.filter(unit => unit.assignmentStatus === 'accepted').length;
    const completedUnits = assignedUnits.filter(unit => unit.status === 'completed').length; // Unit completion status

    return { totalUnits, pendingUnits, activeUnits, completedUnits };
  };

  const summary = getLecturerSummary();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {user.profilePicture ? (
            <img
              src={getImageUrl(user.profilePicture)}
              alt={user.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-primary-100 shadow-sm"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center border-2 border-primary-100 shadow-sm">
              <User className="w-8 h-8 text-primary-600" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome, {user.name}</h1>
            <p className="text-gray-600 mt-1">Tutor/Lecturer Dashboard • Manage your teaching duties</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Units</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalUnits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{summary.pendingUnits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{summary.activeUnits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <UserCheck className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{summary.completedUnits}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Units */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Assigned Units</h2>
          <p className="text-gray-600 mt-1">Units assigned to you for teaching</p>
        </div>

        <div className="p-6">
          {assignedUnits.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No units assigned yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {assignedUnits.map((unit) => {
                // Check if this is a self-paced unit (handled by Subject Pool logic)
                if (unit.learningMode === 'self_paced') {
                  return (
                    <div key={unit.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow bg-blue-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{unit.name}</h3>
                          <p className="text-sm text-gray-600">{unit.course?.name}</p>
                          <p className="text-xs text-blue-600 font-medium mt-1">
                            {unit.currentCohortStatus === 'active' ? '● Class Active' : '● Forming Cohort'}
                          </p>
                        </div>
                        <button
                          onClick={() => navigate('/lecturer-units')}
                          className="bg-white border border-blue-200 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                        >
                          Manage Cohort →
                        </button>
                      </div>
                    </div>
                  );
                }

                // Standard Lecturer-Managed Unit Card
                return (
                  <div key={unit.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{unit.name}</h3>
                        <p className="text-gray-600">{unit.course?.name}</p>
                        <p className="text-sm text-gray-500">{unit.campusName || unit.lecturer?.campus?.name}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(unit.assignmentStatus || unit.status)}`}>
                        {getStatusText(unit.assignmentStatus || unit.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{unit.enrolledStudentsCount || 0}</p>
                        <p className="text-sm text-gray-600">Students</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-900">
                          {unit.classDate
                            ? new Date(unit.classDate).toLocaleDateString()
                            : 'Flexible Schedule'}
                        </p>
                        <p className="text-sm text-gray-600">Start Date</p>
                      </div>
                    </div>

                    {unit.assignmentStatus === 'pending' && (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleAcceptAssignment(unit.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accept Assignment
                        </button>
                        <button
                          onClick={() => handleDeclineAssignment(unit)}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Decline Assignment
                        </button>
                      </div>
                    )}

                    {unit.assignmentStatus === 'accepted' && (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleViewStudents(unit)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Students
                        </button>
                        <button
                          onClick={() => handleManageContent(unit)}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                          <BookOpen className="w-4 h-4" />
                          Manage Content
                        </button>
                      </div>
                    )}

                    {unit.status === 'completed' && (
                      <div className="bg-green-100 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-800">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Unit Completed</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>


      {/* Decline Assignment Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Decline Assignment</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for declining "{selectedUnitForDecline?.name}"
            </p>

            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 resize-none"
              rows={4}
              placeholder="Enter your reason for declining this assignment..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              minLength={10}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitDecline}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
              >
                Submit Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students Modal */}
      {showStudentsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Students for {selectedUnit?.name}
                </h3>
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {fetchingStudents ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-gray-600">Loading students...</span>
                </div>
              ) : studentsForUnit.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No students enrolled in this unit</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {studentsForUnit.map((student) => {
                    const status = getStudentStatus(student);
                    return (
                      <div key={`${student.studentId}-${student.unitId}`} className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
                        {(student.student?.profilePicture || student.Student?.profilePicture) ? (
                          <img
                            src={getImageUrl(student.student?.profilePicture || student.Student?.profilePicture)}
                            alt={student.student?.name || student.Student?.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center border-2 border-white shadow-sm">
                            <User className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-lg">
                                {student.student?.name || student.Student?.name || `Student ${student.studentId}`}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {student.student?.email}
                              </p>
                              {student.student?.phoneNumber && (
                                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" />
                                  {student.student.phoneNumber}
                                </p>
                              )}
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                              {status.text}
                            </span>
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-gray-600">
                                Payment: <span className={`font-medium ${student.paymentStatus === 'confirmed' ? 'text-green-600' :
                                  student.paymentStatus === 'pending' ? 'text-yellow-600' :
                                    student.paymentStatus === 'rejected' ? 'text-red-600' : 'text-gray-600'
                                  }`}>
                                  {student.paymentStatus}
                                </span>
                                {student.paymentMethod && (
                                  <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
                                    {student.paymentMethod}
                                  </span>
                                )}
                              </div>

                              {student.paymentStatus === 'confirmed' && student.status !== 'completed' && (
                                <button
                                  onClick={() => handleCompleteUnitForStudent(student.studentId, student.unitId)}
                                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                                >
                                  Mark as Completed
                                </button>
                              )}
                            </div>

                            {/* Cash Payment Approval Section */}
                            {student.paymentMethod === 'cash' && student.paymentStatus === 'pending' && (
                              <div className="flex gap-2 pt-2 border-t border-gray-100">
                                <button
                                  onClick={() => handleApproveCashPayment(student.studentId, student.unitId)}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  Approve Payment
                                </button>
                                <button
                                  onClick={() => handleRejectCashPayment(student.studentId, student.unitId)}
                                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors flex items-center gap-1"
                                >
                                  <X className="w-3 h-3" />
                                  Reject
                                </button>
                              </div>
                            )}

                            {/* Payment Notes */}
                            {student.paymentNotes && (
                              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                                <strong>Payment Notes:</strong> {student.paymentNotes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LecturerDashboard;
